import React, { useEffect, useRef, useId } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const ConfirmationModal = ({
  isOpen,
  title,
  message,
  warningMessage,
  warningTitle,
  confirmText,
  cancelText,
  submittingText,
  closeLabel,
  confirmVariant = "danger",
  isSubmitting = false,
  onCancel,
  onConfirm,
  confirmTestId = "confirm-action-btn",
  id,
}) => {
  const { t } = useTranslation();
  const generatedId = useId();
  const headingId = id
    ? `${id}-title`
    : `confirmation-modal-title-${generatedId.replace(/:/g, "")}`;

  const modalRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const previousActiveElementRef = useRef(null);

  // Capture previous active element and manage initial focus / restoration
  useEffect(() => {
    if (!isOpen) return undefined;

    previousActiveElementRef.current = document.activeElement;

    const timer = setTimeout(() => {
      if (cancelBtnRef.current && !cancelBtnRef.current.disabled) {
        cancelBtnRef.current.focus();
      } else if (modalRef.current) {
        const focusable = modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
        if (focusable.length > 0) {
          focusable[0].focus();
        }
      }
    }, 0);

    return () => {
      clearTimeout(timer);
      if (
        previousActiveElementRef.current &&
        typeof previousActiveElementRef.current.focus === "function"
      ) {
        previousActiveElementRef.current.focus();
      }
    };
  }, [isOpen]);

  // Handle Tab trapping and Escape
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (!isSubmitting) {
          e.preventDefault();
          e.stopPropagation();
          onCancel();
        }
        return;
      }

      if (e.key === "Tab") {
        if (!modalRef.current) return;
        const focusable = Array.from(
          modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR),
        );
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }

        const firstElement = focusable[0];
        const lastElement = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (
            document.activeElement === firstElement ||
            !modalRef.current.contains(document.activeElement)
          ) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (
            document.activeElement === lastElement ||
            !modalRef.current.contains(document.activeElement)
          ) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isSubmitting, onCancel]);

  if (!isOpen) return null;

  const resolvedCloseLabel = closeLabel || t("common.close", "Close");
  const resolvedWarningTitle = warningTitle || t("common.warning", "Warning");
  const resolvedCancelText = cancelText || t("common.cancel", "Cancel");
  const resolvedConfirmText = confirmText || t("common.confirm", "Confirm");
  const resolvedSubmittingText =
    submittingText || confirmText || t("common.deleting", "Deleting...");

  return (
    <div
      ref={modalRef}
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
          onCancel();
        }
      }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content border-0 shadow" style={{ borderRadius: "8px" }}>
          <div className="modal-header border-bottom-0 pb-0">
            <h5 id={headingId} className="modal-title fw-bold text-dark">
              {title}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onCancel}
              aria-label={resolvedCloseLabel}
              disabled={isSubmitting}
            ></button>
          </div>
          <div className="modal-body py-3 text-secondary" style={{ fontSize: "0.95rem" }}>
            {typeof message === "string" ? <p className="mb-0">{message}</p> : message}

            {warningMessage && (
              <div
                className="alert alert-warning py-2 px-3 mt-3 mb-0"
                style={{ fontSize: "0.85rem" }}
              >
                <div className="d-flex align-items-center mb-1 fw-bold text-warning-emphasis">
                  <i
                    className="bi bi-exclamation-triangle-fill me-2"
                    aria-hidden="true"
                  ></i>
                  {resolvedWarningTitle}
                </div>
                {warningMessage}
              </div>
            )}
          </div>
          <div className="modal-footer border-top-0 pt-0">
            <button
              ref={cancelBtnRef}
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {resolvedCancelText}
            </button>
            <button
              type="button"
              className={`btn btn-${confirmVariant}`}
              data-testid={confirmTestId}
              onClick={onConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  {resolvedSubmittingText}
                </>
              ) : (
                resolvedConfirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.node.isRequired,
  message: PropTypes.node.isRequired,
  warningMessage: PropTypes.node,
  warningTitle: PropTypes.node,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  submittingText: PropTypes.string,
  closeLabel: PropTypes.string,
  confirmVariant: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmTestId: PropTypes.string,
  id: PropTypes.string,
};

export default ConfirmationModal;
